import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Roles } from '../../commonType';
import { loadOfferDetails, setEffortCodeValue, setRadioButtonValue } from '../../features/offerSearch/offerSearchSlice';
import { LOC, PERSON, PIN, TELEPHONE } from '../../util/strings';
import {
  isValidLoc,
  isValidPin,
  LOC_TEXT_BOX_MAX_LENGTH,
  EFFORT_CODE_TEXT_BOX_MAX_LENGTH,
  PIN_TEXT_BOX_MAX_LENGTH,
  // eslint-disable-next-line prettier/prettier
  validEffortCode,
} from '../../util/validation';
import withLocalization from '../hoc/withLocalization';
import withRoles from '../hoc/withRoles';
import './Search.scss';

interface SearchPanelProps {
  localize?: any;
  localizeImages?: any;
  userRole?: any;
}
const Search: FC<SearchPanelProps> = (props: SearchPanelProps) => {
  const { localize, userRole } = props;
  const dispatch = useDispatch();

  const [pinValue, setPinValue] = useState('');
  const [locValue, setLocValue] = useState('');
  const [pinHasError, setpinHasError] = useState(false);
  const [locHasError, setlocHasError] = useState(false);
  const [buttonStatusEnabled, setButtonStatusEnabled] = useState(false);
  const [locPristine, setlocPristine] = useState(true);
  const [pinPristine, setPinPristine] = useState(true);
  const [effortCode, setEffortCode] = useState('');

  const [effortCodeHasError, setEffortCodeHasError] = useState(false);
  const [effortCodeError, setEffortCodeError] = useState('');

  const [selectTouched, setSelectTouched] = useState(false);

  const onPinTouched = () => {
    setPinPristine(false);
  };
  const onLocTouched = () => {
    setlocPristine(false);
  };

  const conversationModeChange = (evt) => {
    const conversationMode = evt.target.value.toLowerCase();
    if (conversationMode === PERSON || conversationMode === TELEPHONE) {
      setSelectTouched(true);
      dispatch(setRadioButtonValue(conversationMode));
    } else {
      setSelectTouched(false);
      setButtonStatusEnabled(false);
    }
  };

  const onEffortCodeChange = (e) => {
    e.preventDefault();
    const effortCode = e.target.value.toUpperCase();
    const errMsg = validEffortCode(effortCode);
    if (errMsg) {
      setEffortCode(effortCode);
      setEffortCodeHasError(true);
      setButtonStatusEnabled(false);
      setEffortCodeError(errMsg);
    } else {
      setEffortCode(effortCode);
      setEffortCodeHasError(false);
      setEffortCodeError(errMsg);
      if ((pinValue || locValue) && selectTouched) {
        setButtonStatusEnabled(true);
      }
    }
  };

  useEffect(() => {
    if (!pinPristine || !effortCodeError) {
      setButtonStatusEnabled(getButtonStatusFA(false, locHasError, locPristine, false, selectTouched));
    } else if (!locPristine || !effortCodeError) {
      setButtonStatusEnabled(getButtonStatusFA(pinHasError, false, false, pinPristine, selectTouched));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTouched]);

  const onFormSubmit = (e) => {
    e.preventDefault();

    let pinProssed = false;
    let locProcessed = false;
    let pinHasValueOnSubmit = false;
    if (pinValue.length > 0) {
      pinHasValueOnSubmit = true;
    }
    if (pinValue && pinValue.length > 0 && isValidPin(pinValue)) {
      pinProssed = true;
      if (effortCode && effortCode.length > 0 && !effortCodeHasError) {
        dispatch(setEffortCodeValue(effortCode));
      }
      dispatch(loadOfferDetails(pinValue.toUpperCase(), PIN));
      setlocHasError(false);
    } else {
      setpinHasError(true);
    }
    if (pinHasValueOnSubmit) {
      setButtonStatusEnabled(false);
      e.preventDefault();
      return;
    } else {
      if (!pinProssed) {
        if (locValue && locValue.length > 0 && isValidLoc(locValue)) {
          if (effortCode && effortCode.length > 0 && !effortCodeHasError) {
            dispatch(setEffortCodeValue(effortCode));
          }
          dispatch(loadOfferDetails(locValue.toUpperCase(), LOC, undefined, undefined, undefined, effortCode));
          setButtonStatusEnabled(false);
          locProcessed = true;
          setpinHasError(false);
        }
      }
    }

    if (!pinProssed && !locProcessed) {
      console.log('Both pin and loc is empty or invalid');
    }
  };

  const onPinChange = (evt) => {
    onPinTouched();
    const pin = evt.target.value;
    let pinError = false;
    if (!isValidPin(pin)) {
      setpinHasError(true);
      pinError = true;
      setPinValue(pin);
    } else {
      setPinValue(pin);
      setpinHasError(false);
    }
    if (!locValue && locHasError) {
      setlocHasError(false);
    }
    setButtonStatusEnabled(
      userRole === Roles.Field_Associate
        ? getButtonStatusFA(pinError, locHasError, locPristine, false, selectTouched)
        : getButtonStatus(pinError, locHasError, locPristine, false),
    );
  };
  const onLocChange = (evt) => {
    onLocTouched();
    const loc = evt.target.value;
    let locError = false;
    if (!isValidLoc(loc)) {
      setlocHasError(true);
      locError = true;
      setLocValue('');
    } else {
      setLocValue(loc);
      setlocHasError(false);
    }
    if (!pinValue && pinHasError) {
      setpinHasError(false);
    }
    setButtonStatusEnabled(
      userRole === Roles.Field_Associate
        ? getButtonStatusFA(pinHasError, locError, false, pinPristine, selectTouched)
        : getButtonStatus(pinHasError, locError, false, pinPristine),
    );
  };

  const getButtonStatus = (pinContainsError, locContainsError, locClean, pinClean): boolean => {
    const enableButtonForPin = !pinClean && !pinContainsError;
    const enableButtonForLoc = !locClean && !locContainsError;
    return enableButtonForLoc || enableButtonForPin;
  };

  //Geting the Button status when the role is Field_Associate
  const getButtonStatusFA = (pinContainsError, locContainsError, locClean, pinClean, selectTouched): boolean => {
    const enableButtonForPin = !pinClean && !pinContainsError && selectTouched;
    const enableButtonForLoc = !locClean && !locContainsError && selectTouched;
    return enableButtonForLoc || enableButtonForPin;
  };

  return (
    <React.Fragment>
      <h5 className="card-title upperCase">
        {localize('preview-sales-engine.static-content.copy.associate-search-page.search-page-header')}
      </h5>
      <hr />
      <p className="card-text">
        {localize('preview-sales-engine.static-content.copy.associate-search-page.search-page-instructions')}
      </p>
      <div>
        <form onSubmit={onFormSubmit}>
          <div className="row">
            <div className="col-md-4 inputWrapper upperCase">
              <label htmlFor="loc">
                {localize('preview-sales-engine.static-content.copy.associate-search-page.package-code')}

                <input
                  type="text"
                  id="loc"
                  name="LOC"
                  onChange={onLocChange}
                  maxLength={LOC_TEXT_BOX_MAX_LENGTH}
                  autoComplete={'off'}
                  list={'autocompleteOff'}
                />
                {locHasError ? (
                  <span className="error">
                    {localize(
                      'preview-sales-engine.static-content.copy.associate-search-page.loc-field-errors#pattern',
                    )}
                  </span>
                ) : (
                  <></>
                )}
              </label>
            </div>
            <div className="col-md-1 centerOr">
              <span className="upperCase">
                {localize('preview-sales-engine.static-content.copy.associate-search-page.or')}
              </span>
            </div>
            <div className="col-md-4 inputWrapper">
              <label htmlFor="pin">
                {localize('preview-sales-engine.static-content.copy.associate-search-page.pin-label')}
                <input
                  type="text"
                  id="pin"
                  name="PIN"
                  onChange={onPinChange}
                  maxLength={PIN_TEXT_BOX_MAX_LENGTH}
                  autoComplete={'off'}
                  list={'autocompleteOff'}
                />
                {pinHasError ? (
                  <span className="error">
                    {localize(
                      'preview-sales-engine.static-content.copy.associate-search-page.pin-field-errors#pattern',
                    )}
                  </span>
                ) : (
                  <></>
                )}
              </label>
            </div>
            <div className="col-md-3 btnTop">
              {buttonStatusEnabled ? (
                <input
                  type="submit"
                  className="btn"
                  value={localize('preview-sales-engine.static-content.copy.associate-search-page.search-button')}
                ></input>
              ) : (
                <input
                  type="submit"
                  className="btn"
                  disabled
                  value={localize('preview-sales-engine.static-content.copy.associate-search-page.search-button')}
                ></input>
              )}
            </div>
          </div>
          {userRole === Roles.Field_Associate ? (
            <div
              className="row"
              style={{
                paddingTop: '20px',
              }}
            >
              <div className="col-sm-5">
                <label htmlFor="converstionMode">
                  <span className="conversation-mode-text">
                    {localize('preview-sales-engine.static-content.copy.Labels.labels#conversation-mode-label')}
                  </span>
                  <span className="asterisk">*</span>
                  {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                  <select onChange={(e) => conversationModeChange(e)} className="conversation-mode-select">
                    <option value="select">
                      {localize('preview-sales-engine.static-content.copy.Labels.labels#please-select-label')}
                    </option>
                    <option value="person">
                      {localize('preview-sales-engine.static-content.copy.Labels.labels#in-person-label')}
                    </option>
                    <option value="telephonic">
                      {localize('preview-sales-engine.static-content.copy.Labels.labels#on-the-phone-label')}
                    </option>
                  </select>
                </label>
              </div>
              <div className="col-sm-5 moodIDWrapper">
                <label htmlFor="moodIdText" style={{ display: 'flex', flexDirection: 'column' }}>
                  <div>
                    <span className="conversation-mode-text">
                      {localize('preview-sales-engine.static-content.copy.associate-search-page.location-code')}
                    </span>
                    <input
                      className="effort-code-input"
                      type="text"
                      defaultValue={effortCode}
                      onChange={onEffortCodeChange}
                      maxLength={EFFORT_CODE_TEXT_BOX_MAX_LENGTH}
                      autoComplete={'off'}
                      list={'autocompleteOff'}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  {effortCodeHasError ? <span className="error">{effortCodeError}</span> : <></>}
                </label>
              </div>
            </div>
          ) : (
            <></>
          )}
        </form>
      </div>
    </React.Fragment>
  );
};
export default withLocalization(withRoles(Search));
